import React from 'react';
import { NextPage } from 'next';
import { getClientIp, slugify } from '@surfline/web-common';

import { fetchOSData } from 'actions/deviceData';
import { fetchCompleteSpotData } from 'actions/spot';
import { fetchFavoriteSubregions, fetchForecasterContent } from 'actions/subregion';
import SpotPage from 'containers/SpotPage';
import { getUser } from 'common/api/user';
import type { AppPageProps } from 'pages/_app';
import { getOrCreateStore } from 'stores';
import { PermissionTypes } from 'types/permissions';
import type { WavetrakContext, WavetrakPageContext } from 'types/wavetrak';
import { forecastDaysAllowed } from 'utils/forecastDaysAllowed';
import getCloudflareCountryCode from 'utils/getCloudflareCountryCode';
import sendRedirect from 'utils/sendRedirect';

const SpotReportPage: NextPage<{}, AppPageProps> = () => <SpotPage />;

SpotReportPage.getInitialProps = async (context: WavetrakPageContext) => {
  const store = getOrCreateStore();
  const { req, res, query } = context;
  const isServer = !!req;
  const cookies = req?.cookies || {};
  const { dispatch, getState } = store;
  const { spotSlug, spotId } = query as {
    spotSlug: string;
    spotId: string;
  };

  const clientIp = getClientIp(context.req) || '';
  const countryCode = getCloudflareCountryCode(req) || '';

  const userData = await getUser(context as WavetrakContext, isServer);

  const has16DayPermission = userData?.permissions?.some(
    (permission) => permission.name === PermissionTypes.CORE_16DAY_FORECAST,
  );

  const includeInternalCams = !!userData?.details?.isInternal;

  const daysAllowed = forecastDaysAllowed({
    type: 'forecast-content',
    isEntitled: has16DayPermission,
    query,
  });

  // Use the centralized spot data fetching action
  await dispatch(
    fetchCompleteSpotData(
      spotId,
      cookies,
      clientIp,
      countryCode,
      (subregionId: string) => {
        // Server-side specific fetching for subregion data
        const subregionPromises = [];

        subregionPromises.push(dispatch(fetchFavoriteSubregions(cookies)));

        // Get the current state to check forecastStatus
        const currentState = getState();
        const spotReport = currentState.spot?.report?.data;

        if (spotReport?.spot?.subregion?.forecastStatus === 'active') {
          subregionPromises.push(
            dispatch(
              fetchForecasterContent({
                cookies,
                days: daysAllowed,
                subregionId,
              }),
            ),
          );
        }

        return Promise.all(subregionPromises);
      },
      includeInternalCams || undefined,
    ),
  );

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      spotSlug,
      spotId,
    });

    await dispatch(fetchOSData(context.req!.headers));
    const state = getState();
    const spot = state.spot.report?.data?.spot;
    if (spot && spot._id !== spotId) {
      sendRedirect(res, `../${slugify(spot.name)}/${spot._id}`, 301);
      return {};
    }

    return {
      enableCssBaseline: true,
      enableTheme: true,
      isAppBannerEnabled: true,
      isSpotPage: true,
      ssrReduxState: state,
    };
  }

  return {
    enableCssBaseline: true,
    enableTheme: true,
    isAppBannerEnabled: true,
    isSpotPage: true,
  };
};

export default SpotReportPage;
